import { CompositionBar, type CompositionSlice } from './CompositionBar';
import { modelColors, rampColor, RAMP_RESIDUAL } from '../styles/chartRamp';
import { allocate } from '../utils/allocate';
import { cents } from '../utils/cents';
import type { CostSlice, UsageResponse } from '../types/api';

// Whole-dollar display string, e.g. "$1,340".
function usd(dollars: number): string {
  return `$${dollars.toLocaleString('en-US')}`;
}

/**
 * Turns one cost breakdown into reconciled `CompositionBar` slices.
 *
 * The client adds a trailing residual slice for whatever the shown slices don't
 * cover (§1). Per-slice dollar amounts come from `allocate()` against the
 * FLOORED headline dollars, so the breakdown sums to the headline exactly — no
 * "$14,001 of parts under a $14,002 headline".
 */
function buildComposition(
  slices: CostSlice[],
  totalCents: number,
  headlineDollars: number,
  residualLabel: (shownCount: number) => string,
  colorFor: (label: string, i: number) => string = (_label, i) => rampColor(i),
): CompositionSlice[] {
  const shown = slices.map((s) => ({ label: s.label ?? s.key ?? 'unknown', cents: cents(s.amountCents) }));
  const shownCents = shown.reduce((a, s) => a + s.cents, 0);

  const parts = shown.map((s) => ({ ...s, residual: false }));
  const residualCents = totalCents - shownCents;
  if (residualCents > 0) {
    parts.push({ label: residualLabel(shown.length), cents: residualCents, residual: true });
  }

  const dollars = allocate(headlineDollars, parts.map((p) => p.cents));
  return parts.map((p, i) => ({
    label: p.label,
    pct: totalCents > 0 ? (p.cents / totalCents) * 100 : 0,
    amount: usd(dollars[i]),
    color: p.residual ? RAMP_RESIDUAL : colorFor(p.label, i),
    residual: p.residual,
  }));
}

/**
 * SpendCard (#630 §1) — headline spend plus by-model and by-project breakdowns,
 * both rendered through one `CompositionBar` off the same ramp. The headline
 * and both breakdowns reconcile to the dollar.
 */
export function SpendCard({ data }: { data: UsageResponse }) {
  const totalCents = cents(data.spendCents);
  const headlineDollars = Math.floor(totalCents / 100);

  const models = data.byModel ?? [];
  const byModelColors = modelColors(models.map((s) => s.label ?? s.key ?? 'unknown'));
  const byModel = buildComposition(
    models,
    totalCents,
    headlineDollars,
    () => 'Other',
    (label, i) => byModelColors.get(label) ?? rampColor(i),
  );
  const byProject = buildComposition(
    data.byProject ?? [],
    totalCents,
    headlineDollars,
    (shownCount) => {
      const more = (data.totalProjects ?? shownCount) - shownCount;
      return more > 0 ? `${more} more project${more === 1 ? '' : 's'}` : 'Other';
    },
  );

  return (
    <div className="gp-card gp-card--hairline">
      <div className="mb-4 flex items-center gap-2">
        <span
          aria-hidden
          className="inline-block rounded-full"
          style={{ width: 6, height: 6, background: 'var(--color-orange-500)' }}
        />
        <span className="text-xs font-semibold uppercase tracking-wider text-orange-600">
          Spend
        </span>
      </div>

      <div className="font-mono text-3xl font-semibold tabular-nums text-navy-900 dark:text-white">
        {usd(headlineDollars)}
      </div>

      <div className="mt-6 grid grid-cols-1 gap-6 sm:grid-cols-2">
        <CompositionBar title="By model" slices={byModel} />
        <CompositionBar title="By project" slices={byProject} />
      </div>
    </div>
  );
}
